import type { UpdateProfilePayload } from 'api/arcMosaico/types'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { fetchProfile, updateProfile } from '../service/profileService'
import type { ProfileFormState, ProfileReadOnlyState } from '../types'
import { parseServerErrors } from './parseServerErrors'

const INITIAL_FORM: ProfileFormState = {
  firstName: '',
  lastName: '',
  email: '',
  username: '',
}

const INITIAL_READ_ONLY: ProfileReadOnlyState = {
  currentProfile: '',
  avatarUrl: '',
  googleConnected: false,
}

/**
 * Hook that manages the user's profile form state.
 * Fetches profile data on mount, provides field update handlers,
 * and handles saving with server-side error parsing.
 *
 * Email is never editable on the user's own profile screen.
 */
export function useProfileForm() {
  const { t } = useTranslation()
  const [form, setForm] = useState<ProfileFormState>(INITIAL_FORM)
  const [readOnly, setReadOnly] = useState<ProfileReadOnlyState>(INITIAL_READ_ONLY)
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState(false)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    let cancelled = false

    async function loadProfile() {
      setLoading(true)
      try {
        const profile = await fetchProfile()
        if (cancelled) return

        setForm({
          firstName: profile.firstName || '',
          lastName: profile.lastName || '',
          email: profile.email || '',
          username: profile.username || '',
        })
        setReadOnly({
          currentProfile: profile.currentProfile || '',
          avatarUrl: profile.avatarUrl || '',
          googleConnected: profile.googleConnected ?? false,
        })
      } catch (err) {
        console.error('[MyProfile] load failed:', err)
        if (!cancelled) setLoadError(true)
      } finally {
        if (!cancelled) setLoading(false)
      }
    }

    loadProfile()
    return () => { cancelled = true }
  }, [])

  const isEmailEditable = false

  /** Updates a form field and clears its validation error */
  function updateField(field: string, value: string) {
    setForm(prev => ({ ...prev, [field]: value }))
    setErrors(prev => {
      if (!prev[field]) return prev
      const { [field]: _, ...rest } = prev
      return rest
    })
  }

  /** Updates the avatar URL after a successful upload */
  function setAvatarUrl(url: string) {
    setReadOnly(prev => ({ ...prev, avatarUrl: url }))
  }

  /** Validates and saves profile data to the API */
  async function handleSave(): Promise<boolean> {
    const nextErrors: Record<string, string> = {}
    if (!form.firstName.trim()) nextErrors.firstName = t('my_profile.first_name_required')
    setErrors(nextErrors)
    if (Object.keys(nextErrors).length > 0) return false

    setSaving(true)
    try {
      const payload: UpdateProfilePayload = {
        firstName: form.firstName,
        lastName: form.lastName,
        username: form.username,
      }
      if (isEmailEditable) {
        payload.email = form.email
      }
      await updateProfile(payload)
      return true
    } catch (err: unknown) {
      console.error('[MyProfile] save failed:', err)
      const serverErrors = parseServerErrors(err)
      if (serverErrors) setErrors(serverErrors)
      return false
    } finally {
      setSaving(false)
    }
  }

  return {
    form, errors, loading, loadError, saving, isEmailEditable,
    avatarUrl: readOnly.avatarUrl,
    currentProfile: readOnly.currentProfile,
    googleConnected: readOnly.googleConnected,
    updateField, setAvatarUrl, handleSave,
  }
}
